import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, LayoutGrid, List, Filter, Calendar, User as UserIcon, Flag, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchArticles, createArticle, updateArticle } from "@/lib/contentOps/api";
import { PIPELINE_COLUMNS, STATUS_META, type ContentStatus } from "@/lib/contentOps/statuses";
import type { ArticleRow } from "@/lib/contentOps/types";

interface Props { onOpenArticle: (id: string) => void }

const CATEGORIES = [
  "Lead Generation", "Outsourcing", "Cold Calling", "Sales Strategy",
  "Digital Marketing", "Business Growth", "AI for Business",
];

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

const StatusPill = ({ status }: { status: ContentStatus }) => {
  const m = STATUS_META[status];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};

const PriorityDot = ({ p }: { p: number }) => {
  const color = p <= 1 ? "hsl(0 75% 60%)" : p <= 2 ? "hsl(28 92% 60%)" : p <= 3 ? "hsl(38 92% 60%)" : "hsl(0 0% 40%)";
  return <span className="inline-flex items-center gap-1 text-[11px]" style={{ color }}><Flag size={10} /> P{p}</span>;
};

const PipelineTab = ({ onOpenArticle }: Props) => {
  const [view, setView] = useState<"kanban" | "table">("kanban");
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [filter, setFilter] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | ContentStatus>("all");
  const [filterLang, setFilterLang] = useState<"all" | "en" | "ar">("all");
  const [dragging, setDragging] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await fetchArticles());
    } catch (e) {
      toast.error("Failed to load articles", { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) =>
    (filter === "" || r.title.toLowerCase().includes(filter.toLowerCase()) || r.slug.toLowerCase().includes(filter.toLowerCase())) &&
    (filterStatus === "all" || r.status === filterStatus) &&
    (filterLang === "all" || r.language === filterLang),
  ), [rows, filter, filterStatus, filterLang]);

  const byStatus = useMemo(() => {
    const map = new Map<ContentStatus, ArticleRow[]>();
    PIPELINE_COLUMNS.forEach((s) => map.set(s, []));
    filtered.forEach((r) => map.get(r.status as ContentStatus)?.push(r));
    return map;
  }, [filtered]);

  const create = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const slug = slugify(newTitle);
      const a = await createArticle({ title: newTitle.trim(), slug, category: newCategory });
      setNewTitle("");
      setRows((r) => [a, ...r]);
      toast.success("Article created");
      onOpenArticle(a.id);
    } catch (e) {
      toast.error("Couldn't create", { description: (e as Error).message });
    } finally {
      setCreating(false);
    }
  };

  const moveTo = async (id: string, status: ContentStatus) => {
    const prev = rows.find((r) => r.id === id);
    if (!prev || prev.status === status) return;
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, status } : r));
    try {
      const patch: Partial<ArticleRow> = { status };
      if (status === "published") patch.published = true;
      await updateArticle(id, patch);
      toast.success(`Moved to ${STATUS_META[status].label}`);
    } catch (e) {
      setRows((rs) => rs.map((r) => r.id === id ? prev : r));
      toast.error("Status update failed", { description: (e as Error).message });
    }
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border p-4"
        style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>New article</label>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Working title…"
            onKeyDown={(e) => e.key === "Enter" && create()}
            className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}
          />
        </div>
        <div className="w-48">
          <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>Category</label>
          <Select value={newCategory} onValueChange={setNewCategory}>
            <SelectTrigger style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={create} disabled={creating || !newTitle.trim()}
          style={{ background: "hsl(207 68% 50%)", color: "white" }}>
          {creating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
          Create
        </Button>
        <div className="ml-auto flex items-end gap-2">
          <div className="rounded-lg border flex" style={{ borderColor: "hsl(0 0% 18%)" }}>
            <button onClick={() => setView("kanban")}
              className="flex items-center gap-1 px-3 py-2 text-xs"
              style={{ background: view === "kanban" ? "hsl(0 0% 12%)" : "transparent", color: "hsl(0 0% 80%)" }}>
              <LayoutGrid size={12} /> Kanban
            </button>
            <button onClick={() => setView("table")}
              className="flex items-center gap-1 px-3 py-2 text-xs"
              style={{ background: view === "table" ? "hsl(0 0% 12%)" : "transparent", color: "hsl(0 0% 80%)" }}>
              <List size={12} /> Table
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={load}
            className="border" style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} style={{ color: "hsl(0 0% 50%)" }} />
        <Input
          placeholder="Search title / slug…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}
        />
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as typeof filterStatus)}>
          <SelectTrigger className="w-44" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}>
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PIPELINE_COLUMNS.concat("refresh_needed").map((s) =>
              <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterLang} onValueChange={(v) => setFilterLang(v as typeof filterLang)}>
          <SelectTrigger className="w-32" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}>
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All langs</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="ar">Arabic</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs ml-auto" style={{ color: "hsl(0 0% 50%)" }}>{filtered.length} of {rows.length}</span>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center h-48" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={18} className="animate-spin mr-2" /> Loading pipeline…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>No articles yet</p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>Create your first article above to begin the editorial pipeline.</p>
        </div>
      ) : view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3">
          {PIPELINE_COLUMNS.map((status) => {
            const items = byStatus.get(status) ?? [];
            const meta = STATUS_META[status];
            return (
              <div key={status}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => { if (dragging) { moveTo(dragging, status); setDragging(null); } }}
                className="flex-shrink-0 w-72 rounded-xl border"
                style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                <div className="flex items-center justify-between px-3 py-2.5 border-b"
                  style={{ borderColor: "hsl(0 0% 12%)" }}>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(0 0% 85%)" }}>
                      {meta.label}
                    </span>
                  </div>
                  <span className="text-[10px] rounded-full px-1.5 py-0.5" style={{ background: "hsl(0 0% 12%)", color: "hsl(0 0% 60%)" }}>
                    {items.length}
                  </span>
                </div>
                <div className="p-2 space-y-2 max-h-[640px] overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="text-[11px] py-4 text-center" style={{ color: "hsl(0 0% 35%)" }}>, </div>
                  ) : items.map((r) => (
                    <div key={r.id}
                      draggable
                      onDragStart={() => setDragging(r.id)}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => onOpenArticle(r.id)}
                      className="rounded-lg border p-2.5 cursor-grab active:cursor-grabbing transition-all hover:border-[hsl(207_68%_50%/0.5)]"
                      style={{ background: "hsl(0 0% 9%)", borderColor: "hsl(0 0% 16%)" }}>
                      <div className="text-sm font-medium leading-snug line-clamp-2 mb-1.5"
                        style={{ color: "hsl(0 0% 92%)" }}>{r.title || "(untitled)"}</div>
                      <div className="flex flex-wrap items-center gap-2 text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>
                        <span>{r.category}</span>
                        <PriorityDot p={r.priority} />
                        {r.language === "ar" && <span className="rounded px-1" style={{ background: "hsl(0 0% 14%)" }}>AR</span>}
                        {typeof r.seo_score === "number" && <span>SEO {r.seo_score}</span>}
                        {r.due_at && <span className="flex items-center gap-0.5"><Calendar size={9} />{new Date(r.due_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide" style={{ color: "hsl(0 0% 55%)", background: "hsl(0 0% 8%)" }}>
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">SEO</th>
                <th className="text-left px-4 py-3">AI conf.</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Open</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t hover:bg-[hsl(0_0%_8%)] cursor-pointer"
                  style={{ borderColor: "hsl(0 0% 12%)", color: "hsl(0 0% 85%)" }}
                  onClick={() => onOpenArticle(r.id)}>
                  <td className="px-4 py-3">
                    <div className="font-medium line-clamp-1">{r.title || "(untitled)"}</div>
                    <div className="text-[11px]" style={{ color: "hsl(0 0% 45%)" }}>/{r.slug}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={r.status as ContentStatus} /></td>
                  <td className="px-4 py-3 text-xs" style={{ color: "hsl(0 0% 60%)" }}>{r.category}</td>
                  <td className="px-4 py-3 text-xs">{r.seo_score ?? ", "}</td>
                  <td className="px-4 py-3 text-xs">{r.ai_confidence ? `${Math.round(r.ai_confidence * 100)}%` : ", "}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: "hsl(0 0% 50%)" }}>{new Date(r.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right"><ExternalLink size={14} style={{ color: "hsl(207 68% 60%)" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PipelineTab;
